import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import InfoIcon from '@mui/icons-material/Info'
import { postCPDataWithoutParams, getCPDataWithoutParams } from 'src/clinic/services'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'

/**
 * Reusable Patient Tags Component
 * 
 * Supports three portals:
 * - Mentor/Practitioner: /api/secure/teacher/patient/tags/* or /api/secure/mentor/patient/tags/*
 * - Clinic Assistant: /api/secure/clinic-assistant/patient/tags/*
 * - Support: /api/secure/cc/support/patient/tags/*
 * 
 * @param {Object} props
 * @param {string} props.patientId - Patient ID
 * @param {string} props.patientTagsApiSignature - Base API path (e.g., '/api/secure/teacher/patient/tags' or '/api/secure/clinic-assistant/patient/tags')
 * @param {Array} props.initialTags - Initial tags from patient data (optional)
 * @param {boolean} props.compact - Compact display mode
 * @param {Function} props.onTagsUpdated - Callback when tags are updated
 * @param {Object} props.sx - Additional styles
 */
export default function PatientTagsComponent({
  patientId,
  patientTagsApiSignature,
  initialTags = [],
  compact = false,
  onTagsUpdated,
  sx = {},
}) {
  const [tags, setTags] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newTagLabel, setNewTagLabel] = useState('')
  const [newTagColor, setNewTagColor] = useState('#6779e3')
  const [newTagNote, setNewTagNote] = useState('')
  const [selectedSuggestion, setSelectedSuggestion] = useState(null)
  const [error, setError] = useState(null)

  // Determine which API service to use based on API signature
  const apiService = useMemo(() => {
    if (!patientTagsApiSignature) return null
    
    if (patientTagsApiSignature.includes('/support/')) {
      return 'support' // Use getSPDataWithParams / putSPDataWithParams
    } else if (patientTagsApiSignature.includes('/clinic-assistant/')) {
      return 'clinic' // Use getCPDataWithoutParams / postCPDataWithoutParams
    } else if (
      patientTagsApiSignature.includes('/teacher/') || 
      patientTagsApiSignature.includes('/mentor/')
    ) {
      return 'mentor' // Use getCPDataWithoutParams / postCPDataWithoutParams (mentor uses clinic services)
    } else {
      return 'clinic' // Default to clinic service
    }
  }, [patientTagsApiSignature])

  // Predefined color options
  const colorOptions = [
    { label: 'Blue', value: '#6779e3' },
    { label: 'Red', value: '#E57373' },
    { label: 'Orange', value: '#FFB74D' },
    { label: 'Yellow', value: '#FDD835' },
    { label: 'Green', value: '#81C784' },
    { label: 'Purple', value: '#BA68C8' },
    { label: 'Pink', value: '#F06292' },
    { label: 'Teal', value: '#4DB6AC' },
  ]

  // Initialize tags from initialTags prop or patient data
  useEffect(() => {
    if (initialTags && Array.isArray(initialTags) && initialTags.length > 0) {
      setTags(initialTags)
    } else if (patientId) {
      // If no initial tags provided, try to load from patient data
      // Tags should come with patient data from the API
      setTags([])
    } else {
      setTags([])
    }
  }, [initialTags, patientId])

  // Load tag suggestions
  const loadSuggestions = async () => {
    if (!patientTagsApiSignature) return

    setLoadingSuggestions(true)
    try {
      const suggestionsEndpoint = `${patientTagsApiSignature}/suggestions?page=1&pageSize=20`
      
      let response
      if (apiService === 'support') {
        response = await getSPDataWithParams(suggestionsEndpoint, {})
      } else {
        // Both clinic-assistant and mentor use getCPDataWithoutParams
        response = await getCPDataWithoutParams(suggestionsEndpoint)
      }

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        const suggestionsData = response?.data?.data?.content || response?.data?.content || []
        setSuggestions(suggestionsData)
      }
    } catch (error) {
      console.error('Error loading suggestions:', error)
      // Don't show error for suggestions, just log it
    } finally {
      setLoadingSuggestions(false)
    }
  }

  // Load suggestions when dialog opens
  useEffect(() => {
    if (showAddDialog && patientTagsApiSignature) {
      loadSuggestions()
    }
  }, [showAddDialog, patientTagsApiSignature])

  // Add tag to patient
  const handleAddTag = async () => {
    if (!newTagLabel.trim() || !patientId || !patientTagsApiSignature) return

    setLoading(true)
    setError(null)
    try {
      const addEndpoint = `${patientTagsApiSignature}/add`
      const payload = {
        patientId,
        label: newTagLabel.trim(),
        color: newTagColor,
        note: newTagNote.trim() || undefined,
      }

      let response
      if (apiService === 'support') {
        // Support portal uses PUT with different structure
        response = await getSPDataWithParams(addEndpoint, payload)
      } else {
        // Both clinic-assistant and mentor use postCPDataWithoutParams
        response = await postCPDataWithoutParams(addEndpoint, payload)
      }

      // Log response for debugging
      console.log('Add tag response:', response)

      // Check for success - handle different response structures
      const isSuccess = 
        response?.status === 200 && (
          isCommonResponseSuccessful(response?.data?.code) ||
          response?.data?.code === undefined || // Some APIs might not return code
          response?.data?.tagCode || // If tagCode exists, it's likely successful
          response?.data?.label // If label exists, it's likely successful
        )

      if (isSuccess) {
        // Extract the tag data - try different possible locations
        const newTag = response?.data?.data || response?.data || {
          tagCode: response?.data?.tagCode,
          label: response?.data?.label || newTagLabel.trim(),
          color: response?.data?.color || newTagColor,
          note: response?.data?.note || newTagNote.trim(),
        }
        
        setTags((prev) => [...prev, newTag])
        toast.success('Tag added successfully')
        setNewTagLabel('')
        setNewTagColor('#6779e3')
        setNewTagNote('')
        setSelectedSuggestion(null)
        setShowAddDialog(false)
        if (onTagsUpdated) {
          onTagsUpdated([...tags, newTag])
        }
      } else {
        // Log the actual response for debugging
        console.error('Add tag failed. Response:', response)
        const errorMsg = response?.data?.message || response?.message || 'Failed to add tag'
        setError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (error) {
      console.error('Error adding tag:', error)
      const errorMsg = error?.response?.data?.message || 'Failed to add tag'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  // Remove tag from patient
  // Note: The API documentation doesn't specify a remove endpoint
  // This functionality is disabled until the endpoint is available
  const handleRemoveTag = async (tagCode) => {
    // TODO: Implement when remove endpoint is available in the API
    console.warn('Tag removal is not yet supported by the API')
    toast.info('Tag removal is not yet available. Please contact support if you need to remove a tag.')
  }

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    if (suggestion) {
      setSelectedSuggestion(suggestion)
      setNewTagLabel(suggestion.label)
      setNewTagColor(suggestion.color || '#6779e3')
      setNewTagNote(suggestion.note || '')
    } else {
      setSelectedSuggestion(null)
      setNewTagLabel('')
      setNewTagColor('#6779e3')
      setNewTagNote('')
    }
  }


  if (!patientTagsApiSignature) {
    return null // Don't render if API signature is not provided
  }

  return (
    <Box sx={{ ...sx }}>
      {/* Tags Display */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: compact ? 0.75 : 1 }}>
        {loading && tags.length === 0 ? (
          <CircularProgress size={16} />
        ) : tags.length > 0 ? (
          <>
            {tags.map((tag) => (
              <Chip
                key={tag.tagCode || tag.label}
                label={tag.label}
                size="small"
                onDelete={undefined} // Tag removal not yet supported by API
                sx={{
                  height: compact ? '20px' : '24px',
                  fontSize: compact ? '10px' : '11px',
                  fontWeight: 500,
                  backgroundColor: tag.color ? `${tag.color}20` : 'rgba(103, 121, 227, 0.1)',
                  color: tag.color || '#6779e3',
                  border: `1px solid ${tag.color ? `${tag.color}40` : 'rgba(103, 121, 227, 0.2)'}`,
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                  },
                }}
                icon={compact ? undefined : <LocalOfferIcon sx={{ fontSize: '12px !important' }} />}
              />
            ))}
            <Tooltip title="Add tag">
              <IconButton
                size="small"
                onClick={() => setShowAddDialog(true)}
                sx={{
                  color: '#6779e3',
                  padding: compact ? '2px' : '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: compact ? 14 : 16 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Add tag to categorize this patient">
            <IconButton
              size="small"
              onClick={() => setShowAddDialog(true)}
              sx={{
                color: '#6779e3',
                padding: compact ? '2px' : '4px',
                '&:hover': {
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                },
              }}
            >
              <AddIcon sx={{ fontSize: compact ? 14 : 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Add Tag Dialog */}
      <Dialog
        open={showAddDialog}
        onClose={() => {
          setShowAddDialog(false)
          setNewTagLabel('')
          setNewTagColor('#6779e3')
          setNewTagNote('')
          setSelectedSuggestion(null)
          setError(null)
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferIcon sx={{ color: '#6779e3' }} />
          Add Patient Tag
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Info Hint */}
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mb: 2, fontSize: '12px' }}
          >
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
              <strong>Tip:</strong> Tags help you quickly categorize and identify patients. 
              You can reuse tags you've created before, or create new ones. 
              Tags are unique per patient per actor, so you can use the same tag name for different patients.
            </Typography>
          </Alert>

          {/* Tag Suggestions */}
          {loadingSuggestions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : suggestions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#64748b' }}>
                Your Recent Tags (click to reuse)
              </Typography>
              <Autocomplete
                options={suggestions}
                getOptionLabel={(option) => option.label || ''}
                value={selectedSuggestion}
                onChange={(event, newValue) => handleSuggestionSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select from your recent tags..."
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '4px',
                        backgroundColor: option.color || '#6779e3',
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.label}
                      </Typography>
                      {option.note && (
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '11px' }}>
                          {option.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {/* Tag Label */}
          <TextField
            autoFocus
            fullWidth
            label="Tag Label *"
            value={newTagLabel}
            onChange={(e) => setNewTagLabel(e.target.value)}
            placeholder="e.g., High Risk, Needs Follow-up, VIP"
            helperText="Enter a descriptive label for this tag"
            sx={{ mb: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newTagLabel.trim()) {
                handleAddTag()
              }
            }}
          />

          {/* Tag Color */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
              Tag Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorOptions.map((color) => (
                <Tooltip key={color.value} title={color.label}>
                  <Box
                    onClick={() => setNewTagColor(color.value)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      backgroundColor: color.value,
                      cursor: 'pointer',
                      border: newTagColor === color.value ? '3px solid #1e293b' : '2px solid transparent',
                      boxShadow: newTagColor === color.value ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          {/* Tag Note */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Note (Optional)"
            value={newTagNote}
            onChange={(e) => setNewTagNote(e.target.value)}
            placeholder="Add any additional context about this tag..."
            helperText="Optional note to provide more context about this tag"
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={() => {
              setShowAddDialog(false)
              setNewTagLabel('')
              setNewTagColor('#6779e3')
              setNewTagNote('')
              setSelectedSuggestion(null)
              setError(null)
            }}
            sx={{
              color: '#64748b',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddTag}
            variant="contained"
            disabled={!newTagLabel.trim() || loading}
            sx={{
              backgroundColor: '#6779e3',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#7450aa',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(103, 121, 227, 0.3)',
              },
            }}
          >
            {loading ? <CircularProgress size={20} /> : 'Add Tag'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
